/**
 * Role Management Utilities
 *
 * Canonical-only after ADR 0073 Phase 5 (GDN-1592). Legacy role-name values
 * (`admin` / `member` / `billing_admin`) have been retired across the
 * backend; production data was backfilled to canonical values.
 *
 * Most authorization checks should now use `useCan(permission)` from
 * `@features/user` instead of these helpers (see ADR 0073). This module
 * remains for display/classification utilities (badges, sort order, etc.)
 * and the small set of consumers that haven't migrated to `useCan` yet.
 */

// Role type definitions
export enum RoleType {
  PRACTICE_ADMINISTRATOR = 'practice_administrator',
  BILLING_ADMINISTRATOR = 'billing_administrator',
  CLINICIAN = 'clinician',
  CLINICAL_STAFF = 'clinical_staff',
  OPERATIONS_SUPPORT = 'operations_support',
  SUPERVISOR = 'supervisor',
}

// Role constants for string-based usage
export const ROLES = {
  PRACTICE_ADMINISTRATOR: 'practice_administrator' as const,
  BILLING_ADMINISTRATOR: 'billing_administrator' as const,
  CLINICIAN: 'clinician' as const,
  CLINICAL_STAFF: 'clinical_staff' as const,
  OPERATIONS_SUPPORT: 'operations_support' as const,
  SUPERVISOR: 'supervisor' as const,
} as const;

// All valid roles
export const VALID_ROLES = new Set<string>(Object.values(ROLES));

// Role classifications. Mirrors backend ROLE_SEAT_CONFIGS: both paid seats,
// mutually exclusive (a user cannot hold clinician and clinical_staff).
export const BILLABLE_ROLES = new Set(['clinician', 'clinical_staff']);

export const ADMIN_EQUIVALENT_ROLES = new Set([
  'operations_support',
  'practice_administrator',
  'supervisor',
]);

export const MEMBER_EQUIVALENT_ROLES = new Set(['clinician']);

// Roles licensed to prescribe in DoseSpot. Mirrors backend Role.PRESCRIBER_ROLES.
// Used to identify which org members a prescribing proxy can act on behalf of
// (GDN-1119).
export const PRESCRIBER_ROLES = new Set(['clinician', 'supervisor']);

export const BILLING_ROLES = new Set(['practice_administrator', 'billing_administrator']);

// Display names for UI
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  practice_administrator: 'Practice Administrator',
  billing_administrator: 'Billing Administrator',
  clinician: 'Clinician',
  clinical_staff: 'Clinical Staff',
  operations_support: 'Operations Support',
  supervisor: 'Supervisor',
};

// Helper type for role strings
export type Role = keyof typeof ROLES | (string & {});

/**
 * Check if user has admin privileges (any admin-equivalent role)
 */
export function hasAdminPrivileges(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => ADMIN_EQUIVALENT_ROLES.has(role));
}

/**
 * Check if user has billing access
 */
export function hasBillingAccess(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => BILLING_ROLES.has(role));
}

/**
 * Check if user has provider/clinician access
 */
export function hasProviderAccess(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => MEMBER_EQUIVALENT_ROLES.has(role));
}

/**
 * Check if user holds a paid seat (clinician or clinical_staff)
 */
export function isBillableUser(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => BILLABLE_ROLES.has(role));
}

/**
 * Get display name for a role
 */
export function getRoleDisplayName(role: string): string {
  return ROLE_DISPLAY_NAMES[role] || role;
}

/**
 * Check if user has a specific (canonical) role.
 */
export function hasRole(roles: string[] | undefined, roleToCheck: string): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.includes(roleToCheck);
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(userRoles: string[] | undefined, rolesToCheck: string[]): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  return rolesToCheck.some(role => hasRole(userRoles, role));
}

/**
 * Check if user has all of the specified roles
 */
export function hasAllRoles(userRoles: string[] | undefined, rolesToCheck: string[]): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  return rolesToCheck.every(role => hasRole(userRoles, role));
}

/**
 * Check if user is view-only admin (has admin privileges but no provider access)
 */
export function isViewOnlyAdmin(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return hasAdminPrivileges(roles) && !hasProviderAccess(roles);
}

/**
 * Sort roles for consistent display order
 */
export function sortRoles(roles: string[]): string[] {
  const roleOrder = [
    'practice_administrator',
    'billing_administrator',
    'supervisor',
    'clinician',
    'clinical_staff',
    'operations_support',
  ];

  return [...roles].sort((a, b) => {
    const indexA = roleOrder.indexOf(a);
    const indexB = roleOrder.indexOf(b);

    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
}

/**
 * Validate if a role string is valid (canonical)
 */
export function isValidRole(role: string): boolean {
  return VALID_ROLES.has(role);
}

/**
 * Returns true if the role is a licensed-prescriber role (clinician/supervisor).
 * Mirrors backend `Role.is_prescriber_role`. Used to identify which org members
 * a prescribing proxy can act on behalf of (GDN-1119).
 */
export function isPrescriberRole(role: string): boolean {
  return PRESCRIBER_ROLES.has(role);
}

/**
 * Mirrors `Role.validate_role_combination` from the backend
 * (`services/ehr_api/.../organizations/models/role.py`) so users see the
 * rule at toggle time instead of a post-submit 400. The only combination
 * rule is that clinician and clinical_staff are mutually exclusive paid
 * seats (GDN-2610); every other combination is valid (GDN-2885 removed
 * the legacy billing_administrator exclusivity, which the signup founder
 * path had always contradicted). Returns true when selecting `role` would
 * create an invalid combination with the current selection
 * (already-selected roles stay enabled so they can be unchecked).
 */
export function isRoleSelectionDisabled(role: string, selectedRoles: Set<string>): boolean {
  if (selectedRoles.has(role)) return false;
  if (role === ROLES.CLINICIAN) return selectedRoles.has(ROLES.CLINICAL_STAFF);
  if (role === ROLES.CLINICAL_STAFF) return selectedRoles.has(ROLES.CLINICIAN);
  return false;
}

/**
 * Get role badge color for UI display
 */
export function getRoleBadgeColor(
  role: string,
): 'primary' | 'secondary' | 'default' | 'error' | 'warning' | 'info' | 'success' {
  if (BILLING_ROLES.has(role)) return 'primary';
  if (ADMIN_EQUIVALENT_ROLES.has(role)) return 'secondary';
  if (MEMBER_EQUIVALENT_ROLES.has(role)) return 'success';
  if (role === ROLES.CLINICAL_STAFF) return 'info';
  return 'default';
}
